/** Web Push on this device: register the service worker, subscribe, store the subscription. */
import { base64UrlDecode } from '@karya/core';
import { env } from '@/lib/env';
import { detectPlatform, deviceLabel, pushSupported } from '@/lib/platform';
import { functionUrl, supabase } from '@/lib/supabase';
import { syncNow } from '@/sync/status';

let registration: Promise<ServiceWorkerRegistration | undefined> | null = null;

/** Register the service worker once (production build, or dev with VITE_PWA_DEV=true). */
export function registerServiceWorker(): Promise<ServiceWorkerRegistration | undefined> {
  if (registration) return registration;
  registration = (async () => {
    if (!('serviceWorker' in navigator)) return undefined;
    if (import.meta.env.DEV && import.meta.env.VITE_PWA_DEV !== 'true') return undefined;
    const url = import.meta.env.DEV ? '/dev-sw.js?dev-sw' : '/sw.js';
    const reg = await navigator.serviceWorker.register(url, {
      type: import.meta.env.DEV ? 'module' : 'classic',
      scope: '/',
    });
    navigator.serviceWorker.addEventListener('message', (e) => {
      if (e.data?.type === 'SYNC_NOW') void syncNow();
      if (e.data?.type === 'NAVIGATE' && typeof e.data.url === 'string')
        window.location.assign(e.data.url);
    });
    return reg;
  })().catch((e) => {
    console.warn('Service worker registration failed', e);
    return undefined;
  });
  return registration;
}

export type PushState = 'unsupported' | 'needs-install' | 'denied' | 'default' | 'granted';

export function pushPermission(): PushState {
  const ios = detectPlatform() === 'ios';
  const standalone =
    (navigator as Navigator & { standalone?: boolean }).standalone === true ||
    window.matchMedia('(display-mode: standalone)').matches;
  if (ios && !standalone) return 'needs-install';
  if (!pushSupported()) return 'unsupported';
  return Notification.permission as PushState;
}

export async function currentSubscription(): Promise<PushSubscription | null> {
  const reg = await registerServiceWorker();
  return (await reg?.pushManager.getSubscription()) ?? null;
}

function withTimeout<T>(p: Promise<T>, ms: number, message: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const t = setTimeout(() => reject(new Error(message)), ms);
    p.then(
      (v) => (clearTimeout(t), resolve(v)),
      (e: unknown) => (clearTimeout(t), reject(e instanceof Error ? e : new Error(String(e)))),
    );
  });
}

/**
 * Must be called from a click/tap (iOS requires a user gesture). Edge and Chrome may ask quietly,
 * with a bell icon in the address bar instead of a pop-up, and the request then waits until the
 * user answers it: `onWaiting` fires when that seems to be happening.
 */
export async function enablePush(
  opts: { onWaiting?: () => void } = {},
): Promise<{ ok: boolean; message: string }> {
  if (!env.vapidPublicKey)
    return {
      ok: false,
      message: 'Push is not configured on this build (VITE_VAPID_PUBLIC_KEY missing).',
    };
  if (!supabase) return { ok: false, message: 'Sign in first.' };
  const state = pushPermission();
  if (state === 'needs-install')
    return {
      ok: false,
      message: 'On iPhone, add Karya to your Home Screen first, then open it from there.',
    };
  if (state === 'unsupported')
    return { ok: false, message: 'This browser can’t receive push notifications.' };
  const waiting = opts.onWaiting ? setTimeout(opts.onWaiting, 2500) : undefined;
  const permission = await Notification.requestPermission().finally(() => clearTimeout(waiting));
  if (permission !== 'granted')
    return {
      ok: false,
      message:
        permission === 'denied'
          ? 'Notifications are blocked for Karya. Allow them in the browser or phone settings, then try again.'
          : 'Notifications were not allowed.',
    };
  const reg = await registerServiceWorker();
  if (!reg)
    return { ok: false, message: 'The service worker could not start. Reload and try again.' };
  await withTimeout(
    navigator.serviceWorker.ready,
    20_000,
    'The app’s background worker didn’t start. Reload the page and try again.',
  );
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await withTimeout(
      reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: base64UrlDecode(env.vapidPublicKey) as BufferSource,
      }),
      30_000,
      'The browser’s push service didn’t answer. Check the internet connection and try again.',
    );
  }
  const json = sub.toJSON() as { endpoint: string; keys: { p256dh: string; auth: string } };
  const { data: user } = await supabase.auth.getUser();
  const { error } = await supabase.from('push_subscriptions').upsert(
    {
      user_id: user.user?.id,
      endpoint: json.endpoint,
      p256dh: json.keys.p256dh,
      auth: json.keys.auth,
      user_agent: deviceLabel(),
      platform: detectPlatform(),
      last_seen_at: new Date().toISOString(),
      failed_count: 0,
      deleted_at: null,
    },
    { onConflict: 'endpoint' },
  );
  if (error) return { ok: false, message: `Couldn’t save this device: ${error.message}` };
  return { ok: true, message: 'Notifications are on for this device.' };
}

export async function disablePush(): Promise<void> {
  const sub = await currentSubscription();
  if (sub) {
    await supabase?.from('push_subscriptions').delete().eq('endpoint', sub.endpoint);
    await sub.unsubscribe();
  }
}

export async function sendTestNotification(): Promise<{
  devices: number;
  delivered: number;
  failed: number;
  errors?: string[];
}> {
  const { data } = await supabase!.auth.getSession();
  const res = await fetch(functionUrl('send-notifications'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${data.session?.access_token ?? ''}`,
    },
    body: JSON.stringify({ action: 'test' }),
  });
  const body = await res.json();
  if (!res.ok) throw new Error(body.error ?? `Error ${res.status}`);
  return body;
}

export async function sendSummaryNow(): Promise<void> {
  const { data } = await supabase!.auth.getSession();
  const res = await fetch(functionUrl('daily-summary'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${data.session?.access_token ?? ''}`,
    },
    body: '{}',
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error ?? `Error ${res.status}`);
}

/** Keep the app icon badge equal to the unread Inbox count. */
export async function updateBadge(count: number): Promise<void> {
  const nav = navigator as Navigator & {
    setAppBadge?: (n?: number) => Promise<void>;
    clearAppBadge?: () => Promise<void>;
  };
  try {
    if (count > 0) await nav.setAppBadge?.(count);
    else await nav.clearAppBadge?.();
  } catch {
    /* best effort */
  }
}
